import { Component, effect, inject, Signal } from '@angular/core';

import { CmsSections } from '@shared/cms/cms-sections';
import { LandingCmsService } from '@core/cms/landing-cms.service';
import { LandingSectionGroup, LandingSeo } from '@core/cms/landing.cms';
import { SeoService } from '@core/seo/seo.service';

@Component({
    selector: 'app-home',
    imports: [CmsSections],
    templateUrl: './home.html',
    styleUrl: './home.scss',
})
export class Home {
    private readonly _landing: LandingCmsService = inject(LandingCmsService);
    private readonly _seo: SeoService = inject(SeoService);

    /** Tyme Group landing sections from the CMS, grouped by `blockId` for banded rendering. */
    protected readonly cmsGroups: Signal<ReadonlyArray<LandingSectionGroup>> =
        this._landing.sectionGroups;

    /** CMS `shared.seo` for the active language; re-runs when the language changes. */
    private readonly _cmsSeo: Signal<LandingSeo | null> = this._landing.seo;

    /** Whether the landing CMS request is in flight. */
    private readonly _isLoading: Signal<boolean> = this._landing.landing.isLoading;

    constructor() {
        // Apply CMS SEO as soon as (and whenever) it resolves — runs during SSR so the meta tags
        // land in the server-rendered HTML that crawlers read. Falls back to the i18n + asset-logo
        // defaults when the CMS has no `seo` component configured.
        effect((): void => {
            const seo: LandingSeo | null = this._cmsSeo();

            if (seo) {
                this._seo.applyCmsSeo(seo);

                return;
            }

            // No CMS SEO *yet* is not the same as no CMS SEO. Strapi answers with
            // `Cache-Control: no-store`, which makes Angular's HTTP transfer cache refuse to store
            // the response, so the client refetches the landing after hydration. Applying the
            // defaults in that window overwrote the correct, server-rendered CMS title and
            // description for the length of the request — a visible flash in the tab. Leave the
            // rendered tags alone until the resource settles; an errored or empty result still
            // falls through to the defaults below.
            if (this._isLoading()) {
                return;
            }

            this._seo.applyDefaultSeo();
        });
    }
}
